import * as React from 'react';
import { useMutation } from '@apollo/client';
import { Button, Loader, Panel, PanelHeader, PanelBody, PanelFooter, PanelRow, PanelCell } from '@teamsnap/teamsnap-ui';
import Modal from '../../components/common/modal';

import FormElement from '../../components/form/form-element-hook';
import * as ConnectDivisionToOrganization from '../../core/gql/mutations/connect-division-to-organization';

import { useForm } from 'react-hook-form';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';

const ConnectElement = FormElement as new () => FormElement;

export interface Props {
  shouldShowModal: boolean;
  setShouldShowModal: (shouldShowModal) => void;
  organizationId: number | null;
}

const OrganizationConnect: React.FunctionComponent<Props> = ({
  shouldShowModal,
  setShouldShowModal,
  organizationId,
}) => {
  const [connectOrganization, connectOrganizationResponse] = useMutation(ConnectDivisionToOrganization.MUTATION);

  const submit = async (values) => {
    try {
      await connectOrganization({
        variables: { divisionId: values.divisionId, organizationId: organizationId },
      });

      reactiveFeedbackMessage({
        message: 'Organization connected.',
        autoclose: true,
        isError: false,
      });
      setShouldShowModal(false);
    } catch ({ graphQLErrors }) {
      if (graphQLErrors) {
        const errors: Array<String> = graphQLErrors.map(({ message }) => message);
        reactiveFeedbackMessage({
          message: errors.join('. '),
          autoclose: true,
          isError: true,
        });
      }
    }
  };

  const isSubmitting = connectOrganizationResponse.loading;

  const organizationConnectForm = useForm();
  const { register, handleSubmit } = organizationConnectForm;

  return (
    <Modal isOpen={shouldShowModal} onClose={() => setShouldShowModal(false)}>
      <div id="organization-connect-editor-content">
        <form onSubmit={handleSubmit(submit)}>
          <Panel mods="Panel--form">
            <PanelHeader title="Division Details" />
            <PanelBody>
              <PanelRow isWithCells={true} mods="Panel-row--header">
                <PanelCell mods="Panel-cell--header u-size3of12 u-flex u-flexAlignItemsCenter">
                  <h4>Division ID</h4>
                </PanelCell>
                <PanelCell mods="u-size9of12">
                  <ConnectElement
                    useHookResponse={organizationConnectForm}
                    inputRef={register({
                      required: 'You must enter the division\'s id.',
                    })}
                    label="Division Id"
                    labelHidden={true}
                    type="text"
                    field="divisionId"
                    placeholder="Division Id"
                  />
                </PanelCell>
              </PanelRow>
              <PanelFooter mods="u-textLeft">
                <div className="button-list">
                  <Button mods="Button--negative" icon="dismiss" onClick={() => setShouldShowModal(false)}>
                    Cancel
                  </Button>
                  <Button isDisabled={isSubmitting} color="primary" type="submit" icon={isSubmitting ? null : 'check'}>
                    {isSubmitting && <Loader type="spin" style={{ width: '13px', height: '13px' }} />}
                    Connect
                  </Button>
                </div>
              </PanelFooter>
              <PanelFooter mods="u-textLeft">
                <div>
                  To be used to connect a Snapi Division to this Organization. The Division must be a root division.
                </div>
              </PanelFooter>
            </PanelBody>
          </Panel>
        </form>
      </div>
    </Modal>
  );
};

export default OrganizationConnect;
